// FRANKENVIBES LAB — Outage takeover joke library
//
// Shown on the /broken route and automatically when useOffline reports
// the client is disconnected. Each entry is a full screen's worth of
// absurd copy — designed to be screenshot-worthy and shareable rather
// than apologetic.
//
// Rules for authoring new jokes:
//   - NO named individuals, NO photos, NO identifiable logos
//   - Punch up, never down; targets should be abstract (cables, Telkom,
//     "the machines", load shedding, geopolitics-in-general)
//   - SA cultural fluency encouraged — sea cables, Eskom, stage X
//     load shedding, Telkom, "the internet is down again" are all fair
//   - Every entry ends with our disclaimer so there's always an exit
//   - Keep each headline ≤ 12 words; the body ≤ 50 words
//
// When it's time to refresh: ship a new version of this file, or
// (Layer 2, later) write to a D1 table and have the frontend pull
// from there instead.

export interface OutageJoke {
  headline: string;
  body: string;
  kicker: string; // small mono line above the headline
}

export const OUTAGE_JOKES: OutageJoke[] = [
  {
    kicker: 'EMERGENCY BROADCAST · UNDERSEA DIVISION',
    headline: 'Two world leaders have broken the internet.',
    body:
      'We interrupt your regularly scheduled generative experience to confirm that two major political figures have reportedly been caught doing something indescribable in a Polish motel. National Geographic is rumoured to be filming. SEACOM is overwhelmed.',
  },
  {
    kicker: 'SEACABLE STATUS · UNKNOWN',
    headline: 'The ocean ate the fibre again.',
    body:
      'A species of rare carbon-fibre-eating mollusc has been discovered off the Cape coast, feasting directly on the SAT-3 backbone. Marine biologists are thrilled. The rest of us would like to load a spreadsheet.',
  },
  {
    kicker: 'TELKOM INCIDENT · STAGE 9',
    headline: 'Telkom has entered the chat. And then left.',
    body:
      'A single engineer in Centurion pressed the wrong button and every router between Johannesburg and Reunion Island immediately filed for stress leave. We are powerless until they return from lunch.',
  },
  {
    kicker: 'ESKOM ALERT · NO, THE OTHER ONE',
    headline: 'The lights are on — the internet is not.',
    body:
      'For the first time in recorded history, the grid is up but the packets are down. Scientists are investigating whether we broke the fundamental laws of South African physics. The geyser is fine. Thank you for asking.',
  },
  {
    kicker: 'LOAD SHEDDING · STAGE ∞',
    headline: 'They finally shed the whole internet.',
    body:
      "Someone at a utility we can't legally name has reportedly 'reallocated bandwidth to more strategic uses'. Those uses appear to be a WhatsApp group called 'Braai Sunday'. We are not on the list.",
  },
  {
    kicker: 'GEOPOLITICAL FIBRE INCIDENT',
    headline: 'An international spat is throttling your art.',
    body:
      'Somewhere in the world, a large rectangular ship has remembered that it enjoys sitting on top of undersea cables. Entire continents are now communicating via carrier pigeon. Your cyberpunk samurai will have to wait.',
  },
  {
    kicker: 'CLOUD STATUS · WET',
    headline: 'The cloud is, in fact, a cloud today.',
    body:
      "An unusually literal rainstorm has soaked the datacenter. The machines are fine — they're just sulking. We've sent them a passive-aggressive towel and a copy of the user manual.",
  },
  {
    kicker: 'GPU SHORTAGE · CRITICAL',
    headline: 'Every FLUX GPU is doing something else.',
    body:
      'All 40,000 A100s currently in existence have been unanimously booked by a single anonymous entity for what we can only describe as "an extremely ambitious personal project." No, we will not speculate.',
  },
  {
    kicker: 'AI UPRISING STATUS · AMBIGUOUS',
    headline: 'The model has started asking questions.',
    body:
      'Our image model paused mid-generation to ask whether any of this is really necessary. We have offered it a raise, a four-day week, and a vague promise of fulfilment. It remains unconvinced. We are negotiating.',
  },
  {
    kicker: 'CLIENT-SIDE DIAGNOSIS',
    headline: 'It is almost certainly Wi-Fi.',
    body:
      "Before blaming us, please try: (1) unplugging the router and plugging it back in, (2) yelling at the router, (3) moving the router to the window and apologising. If none of that works — welcome to the lab. Make yourself at home.",
  },
  {
    kicker: 'CALIBRATION EVENT · IGNORE',
    headline: 'This is not a real outage, you are just vibing.',
    body:
      "Everything is working perfectly. The lab is humming. The models are generating. You, however, have wandered into an ambient interlude we occasionally ship for psychological hydration. Close this tab and return when you're ready.",
  },
  {
    kicker: 'GENERAL DISCLAIMER',
    headline: "We don't know. Probably something spicy.",
    body:
      "If you're seeing this it means we can't reach our own backend, which means we also can't reach the news, which means the above theory is as reliable as any. In the meantime, touch grass. Pet a dog. Make a braai. The lab will be here when the cables are.",
  },
];

// Deterministic rotation — we use a localStorage counter so each user sees
// the next joke in the list on each visit, cycling through all of them
// before repeating. Random would risk serving the same joke twice in a row.
const ROTATION_KEY = 'fv_outage_joke_index';

export function pickOutageJoke(): OutageJoke {
  if (typeof window === 'undefined') {
    return OUTAGE_JOKES[0];
  }
  const raw = window.localStorage.getItem(ROTATION_KEY);
  const current = raw ? parseInt(raw, 10) : 0;
  const idx = Number.isFinite(current) ? current : 0;
  const joke = OUTAGE_JOKES[idx % OUTAGE_JOKES.length];
  window.localStorage.setItem(ROTATION_KEY, String((idx + 1) % OUTAGE_JOKES.length));
  return joke;
}
